import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2, Check, IndianRupee, Undo2, X } from 'lucide-react';
import type {
  AbsenceBulkRulingResult,
  AbsencePersonSummary,
  AbsenceRuling,
  AttendanceDayRow,
} from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const absencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/absences',
  component: AbsencesPage,
});

const RULING_COPY: Record<AbsenceRuling, { title: string; blurb: string; confirm: string }> = {
  lop: {
    title: 'Count as loss of pay',
    blurb:
      'Records an approved unpaid-leave day for each selected date, so payroll treats them as LOP. Excusing a day later takes that back.',
    confirm: 'Count as LOP',
  },
  excuse: {
    title: 'Excuse these days',
    blurb: 'The days stay paid and drop off this list. Use it when the absence was accounted for.',
    confirm: 'Excuse',
  },
  'allow-backfill': {
    title: 'Allow them to log these days',
    blurb:
      'Lets this person file the sessions they missed, with the usual split rules. Each permission is spent once they use it.',
    confirm: 'Allow',
  },
};

const BULK_CHUNK = 200;

function AbsencesPage(): JSX.Element {
  const queryClient = useQueryClient();
  // A queue item links here with the person in the hash so it opens on them.
  const [chosenPersonId, setChosenPersonId] = React.useState<string | null>(
    () => window.location.hash.slice(1) || null,
  );
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = React.useState<AbsenceRuling | null>(null);
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [failures, setFailures] = React.useState<AbsenceBulkRulingResult['failed']>([]);

  const summaryQuery = useQuery({
    queryKey: ['attendance', 'absences', 'summary'],
    queryFn: () => api.getAbsencesSummary(),
  });
  const people = summaryQuery.data?.people ?? [];

  // Whoever has the most to rule on is the default, and a person who has just
  // been cleared falls out of the list so the next one takes their place.
  const person = people.find((p) => p.personId === chosenPersonId) ?? people[0] ?? null;
  const personId = person?.personId ?? null;

  const daysQuery = useQuery({
    queryKey: ['attendance', 'absences', 'person', personId],
    queryFn: () => api.listOpenAbsences(personId!),
    enabled: personId !== null,
  });
  const rows = daysQuery.data ?? [];

  React.useEffect(() => {
    setSelected(new Set());
    setFailures([]);
  }, [personId]);

  React.useEffect(() => {
    if (pending) setNote('');
  }, [pending]);

  const rule = useMutation({
    mutationFn: async (v: { ruling: AbsenceRuling; dates: string[]; note: string }) => {
      if (!personId) throw new Error('No person selected');
      const trimmed = v.note.trim() || undefined;
      const result: AbsenceBulkRulingResult = { done: 0, failed: [] };
      for (let i = 0; i < v.dates.length; i += BULK_CHUNK) {
        const part = await api.ruleOnAbsences({
          ruling: v.ruling,
          days: v.dates.slice(i, i + BULK_CHUNK).map((d) => ({ personId, businessDate: d })),
          note: trimmed,
        });
        result.done += part.done;
        result.failed.push(...part.failed);
      }
      return result;
    },
    onSuccess: async (result) => {
      setError(null);
      setPending(null);
      setFailures(result.failed);
      setSelected(new Set(result.failed.map((f) => f.businessDate)));
      await queryClient.invalidateQueries({ queryKey: ['attendance', 'absences'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const rulable = React.useMemo(() => rows.filter((r) => !r.backfillGranted), [rows]);
  const months = React.useMemo(() => groupByMonth(rows), [rows]);

  const toggleDay = (date: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };
  const setMany = (dates: string[], on: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const d of dates) {
        if (on) next.add(d);
        else next.delete(d);
      }
      return next;
    });
  };

  const total = summaryQuery.data?.totalDays ?? 0;

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
          Missed clock-ins
        </h1>
        <p className="max-w-2xl text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">
          Days someone was expected to work but never clocked in. Pick a person, tick the days, and
          rule on them together — count them as loss of pay, excuse them, or let the person log the
          days they missed. Nothing affects pay until you decide.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not record that</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {summaryQuery.isPending ? (
        <p className="py-16 text-center text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
      ) : people.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[hsl(var(--border))] py-12 text-center">
              <CalendarX2 className="size-5 text-[hsl(var(--muted-foreground))]" />
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Everyone has clocked in, or every missed day has been ruled on.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Waiting on a decision</CardTitle>
              <CardDescription>
                {total} {total === 1 ? 'day' : 'days'} across {people.length}{' '}
                {people.length === 1 ? 'person' : 'people'}.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-1">
              {people.map((p) => (
                <PersonRow
                  key={p.personId}
                  person={p}
                  active={p.personId === personId}
                  onPick={() => setChosenPersonId(p.personId)}
                />
              ))}
            </CardContent>
          </Card>

          {person ? (
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex flex-col gap-1.5">
                    <CardTitle className="text-base">
                      {person.personName ?? person.personId}
                    </CardTitle>
                    <CardDescription>
                      {person.openDays} {person.openDays === 1 ? 'day' : 'days'} to rule on, from{' '}
                      {formatDay(person.oldest)} to {formatDay(person.newest)}.
                      {person.backfillGranted > 0
                        ? ` ${person.backfillGranted} already allowed to log.`
                        : ''}
                    </CardDescription>
                  </div>
                  {rulable.length > 0 ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setMany(
                            rulable.map((r) => r.businessDate),
                            true,
                          )
                        }
                        disabled={selected.size === rulable.length}
                      >
                        Select all
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelected(new Set())}
                        disabled={selected.size === 0}
                      >
                        Clear
                      </Button>
                    </div>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                {failures.length > 0 ? (
                  <Alert variant="destructive">
                    <AlertTitle>
                      {failures.length} {failures.length === 1 ? 'day was' : 'days were'} not
                      recorded
                    </AlertTitle>
                    <AlertDescription>
                      <ul className="mt-1 flex flex-col gap-0.5">
                        {failures.map((f) => (
                          <li key={f.businessDate}>
                            {formatDay(f.businessDate)} — {f.message}
                          </li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                ) : null}

                {daysQuery.isPending ? (
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
                ) : (
                  months.map((m) => (
                    <MonthSection
                      key={m.key}
                      month={m}
                      selected={selected}
                      onToggle={toggleDay}
                      onSetAll={(on) =>
                        setMany(
                          m.rows.filter((r) => !r.backfillGranted).map((r) => r.businessDate),
                          on,
                        )
                      }
                    />
                  ))
                )}

                {selected.size > 0 ? (
                  <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 shadow-lg">
                    <span className="text-sm font-medium">
                      {selected.size} {selected.size === 1 ? 'day' : 'days'} selected
                    </span>
                    <div className="ml-auto flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPending('allow-backfill')}
                      >
                        <Undo2 className="size-4" />
                        Let them log
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPending('excuse')}>
                        <Check className="size-4" />
                        Excuse
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPending('lop')}>
                        <IndianRupee className="size-4" />
                        Loss of pay
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Clear selection"
                        onClick={() => setSelected(new Set())}
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => (!open ? setPending(null) : undefined)}
      >
        <DialogContent className="max-w-md">
          {pending && person ? (
            <>
              <DialogHeader>
                <DialogTitle>{RULING_COPY[pending].title}</DialogTitle>
                <DialogDescription>
                  {selected.size} {selected.size === 1 ? 'day' : 'days'} for{' '}
                  {person.personName ?? person.personId}. {RULING_COPY[pending].blurb}
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  rule.mutate({ ruling: pending, dates: [...selected].sort(), note });
                }}
                className="flex flex-col gap-5"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ruling-note">Note (optional)</Label>
                  <Input
                    id="ruling-note"
                    autoFocus
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    placeholder="Recorded against each day"
                    maxLength={500}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setPending(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={rule.isPending}>
                    {rule.isPending ? 'Saving…' : RULING_COPY[pending].confirm}
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function PersonRow({
  person,
  active,
  onPick,
}: {
  person: AbsencePersonSummary;
  active: boolean;
  onPick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]',
        active
          ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand))]/[0.06]'
          : 'border-transparent hover:bg-[hsl(var(--muted))]/60',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-[hsl(var(--foreground))]">
          {person.personName ?? person.personId}
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          since {formatDay(person.oldest)}
        </div>
      </div>
      <Badge variant={active ? 'default' : 'muted'} className="tabular-nums">
        {person.openDays}
      </Badge>
    </button>
  );
}

interface MonthGroup {
  key: string;
  label: string;
  rows: AttendanceDayRow[];
}

function MonthSection({
  month,
  selected,
  onToggle,
  onSetAll,
}: {
  month: MonthGroup;
  selected: ReadonlySet<string>;
  onToggle: (date: string) => void;
  onSetAll: (on: boolean) => void;
}): JSX.Element {
  const rulable = month.rows.filter((r) => !r.backfillGranted);
  const picked = rulable.filter((r) => selected.has(r.businessDate)).length;
  const all = rulable.length > 0 && picked === rulable.length;
  const checkboxRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = picked > 0 && !all;
  }, [picked, all]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--border))] pb-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <input
            ref={checkboxRef}
            type="checkbox"
            className="size-4 accent-[hsl(var(--brand))]"
            checked={all}
            disabled={rulable.length === 0}
            onChange={(e) => onSetAll(e.currentTarget.checked)}
          />
          {month.label}
        </label>
        <span className="text-xs tabular-nums text-[hsl(var(--muted-foreground))]">
          {picked > 0 ? `${picked} of ${month.rows.length}` : month.rows.length}{' '}
          {month.rows.length === 1 ? 'day' : 'days'}
        </span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(4.75rem,1fr))] gap-2">
        {month.rows.map((row) => (
          <DayChip
            key={row.businessDate}
            row={row}
            checked={selected.has(row.businessDate)}
            onToggle={() => onToggle(row.businessDate)}
          />
        ))}
      </div>
    </section>
  );
}

function DayChip({
  row,
  checked,
  onToggle,
}: {
  row: AttendanceDayRow;
  checked: boolean;
  onToggle: () => void;
}): JSX.Element {
  const date = new Date(`${row.businessDate}T00:00:00.000Z`);
  const weekday = date.toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'UTC' });
  const title = [
    formatDay(row.businessDate, true),
    row.backfillGranted ? 'Allowed to log this day' : null,
    row.note,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={row.backfillGranted}
      onClick={onToggle}
      title={title}
      className={cn(
        'flex flex-col items-center gap-0.5 rounded-lg border px-2 py-2 text-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]',
        checked
          ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/10'
          : 'border-[hsl(var(--border))] hover:border-[hsl(var(--brand))]/40',
        row.backfillGranted && 'cursor-not-allowed opacity-50',
      )}
    >
      <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        {weekday}
      </span>
      <span className="text-lg font-semibold leading-none tabular-nums text-[hsl(var(--foreground))]">
        {date.getUTCDate()}
      </span>
      <span className="max-w-full truncate text-[10px] text-[hsl(var(--muted-foreground))]">
        {row.backfillGranted ? 'allowed' : (row.note ?? '')}
      </span>
    </button>
  );
}

function groupByMonth(rows: AttendanceDayRow[]): MonthGroup[] {
  const byKey = new Map<string, AttendanceDayRow[]>();
  for (const r of rows) {
    const key = r.businessDate.slice(0, 7);
    const list = byKey.get(key);
    if (list) list.push(r);
    else byKey.set(key, [r]);
  }
  return [...byKey.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([key, list]) => ({
      key,
      label: new Date(`${key}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
      rows: [...list].sort((a, b) => (a.businessDate < b.businessDate ? -1 : 1)),
    }));
}

function formatDay(iso: string, withYear = false): string {
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}
